<template>
  <div
    class="graphComparison graphComparison1 graphComparison--1 lp-section width-expand padding-2xl-vertical l:padding-max-vertical"
    :class="`bg-${backgroundColor} c-${textColor}`"
    :id="`GraphComparison1-${pageUid}`">
    <div class="section-container container container-l padding-xl-horizontal">
      <div class="section-titles title-block text-center">
        <h2
          class="section-title block margin-remove"
          :class="`c-${titleColor}`"
          v-if="isNonEmptyString(title)">
          {{ title }}
        </h2>
        <h3
          class="section-sub-title block"
          :class="`c-${subTitleColor}`"
          v-if="isNonEmptyString(subTitle)">
          {{ subTitle }}
        </h3>
      </div>

      <div class="container container-s">
        <div class="comparison-graph cg">
          <div class="main-graph-area overflow-hidden">
            <div
              class="tooltip-area"
              :style="`width: ${tooltipLinePositionPercent}%`">
              <div class="line-indicator"></div>
              <div
                :class="`c-${tooltipTextColor} bg-${tooltipBackgroundColor}`"
                class="tooltip flex-center flex-middle padding-m border-radius-s text-center">
                <div class="tooltip__content">
                  <span>{{ lineTooltipText }}</span>
                </div>
                <div class="tooltip__arrow"></div>
              </div>
            </div>
            <div class="graph-area">
              <div
                :class="`c-${graph1TextColor} bg-${graph1BackgroundColor}`"
                class="bar bar--0 flex flex-between flex-middle flex-center padding-xl-horizontal"
                :style="`width: ${graph1WidthPercent}%`">
                <div class="bar__text">
                  <div
                    class="bar__text-main text-xl semibold"
                    v-if="isNonEmptyString(graph1Title)">
                    <span>{{ graph1Title }}</span>
                  </div>
                  <div
                    class="bar__text-secondary text-s"
                    v-if="isNonEmptyString(graph1Text)">
                    <span>{{ graph1Text }}</span>
                  </div>
                </div>
                <div
                  class="bar__number text-max semibold ltr"
                  v-if="isNonEmptyString(graph1AmountText)">
                  <span>{{ graph1AmountText }}</span>
                </div>
              </div>

              <div
                :class="`c-${graph2TextColor} bg-${graph2BackgroundColor}`"
                class="bar bar--1 flex flex-between flex-middle flex-center padding-xl-horizontal"
                :style="`width: ${graph2WidthPercent}%`">
                <div class="bar__text">
                  <div
                    class="bar__text-main text-xl semibold"
                    v-if="isNonEmptyString(graph2Title)">
                    <span>{{ graph2Title }}</span>
                  </div>
                  <div
                    class="bar__text-secondary text-s"
                    v-if="isNonEmptyString(graph2Text)">
                    <span>{{ graph2Text }}</span>
                  </div>
                </div>
                <div
                  class="bar__number text-max semibold ltr"
                  v-if="isNonEmptyString(graph2AmountText)">
                  <span>{{ graph2AmountText }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="scales-area flex">
            <div
              class="scale-unit"
              v-for="step of graphLegendSteps"
              :style="`margin-inline-end: ${step.percentSize}%`">
              <div class="scale-unit__marker"></div>
              <div
                :class="`c-${lineColor}`"
                class="scale-unit__text ">
                <span>{{ translateNumber(step.number) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="graph-legal container container-s margin-xl-top"
        v-if="isNonEmptyString(legalText) || legalLink.show">
        <div class="max-width-700 margin-auto-horizontal text-justify">
          <div class="legal" style="">
            <p :class="`c-${legalTextColor}`">
              {{ legalText }}
              <span v-if="legalLink.show">
                <span>&nbsp;</span>
                <a
                  :class="`c-${legalLinkColor}`"
                  v-bind="legalLink"
                  @click.prevent="legalLink.navigationHandler">
                  {{ legalLink.text }}
                </a>
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import useUiElements from "@/client/extensions/composition/useUiElements";
import lpSectionMixin from "@/client/applications/meitavlp/extensions/mixins/lpSection";
import { inject } from "vue";

export default {
  mixins: [lpSectionMixin],

  setup(props) {
    let pageUid = inject("pageUid");

    let schema = {
      title: "",
      subTitle: "",
      totalAmount: 0,
      graph1Amount: 0,
      graph2Amount: 0,
      lineAmount: 0,
      graph1Title: "",
      graph1Text: "",
      graph1AmountText: "",
      graph2Title: "",
      graph2Text: "",
      graph2AmountText: "",
      lineTooltipText: "",
      legalText: "",
      legalLink: {},
      backgroundColor: "",
      titleColor: "",
      subTitleColor: "",
      graph1BackgroundColor: "",
      graph1TextColor: "",
      graph2BackgroundColor: "",
      graph2TextColor: "",
      tooltipBackgroundColor: "",
      tooltipTextColor: "",
      lineColor: "",
      legalTextColor: "",
      legalLinkColor: "",
    };
    let overloadedProps = useUiElements({ input: props.sectionData }).props;
    return {
      ...schema,
      ...overloadedProps,
      allData: overloadedProps,
      pageUid,
      trackEvent: inject("trackEvent"),
      track: inject("track"),
      triggerSectionsEvent: inject("triggerSectionsEvent"),
    };
  },
  data: function () {
    return {
      graphLegendItemCount: 8,
      graphLegendScaleResolution: 500,
    };
  },
  computed: {
    // computions
    graph1WidthPercent() {
      return (this.graph1Amount / this.totalAmount) * 100;
    },
    graph2WidthPercent() {
      return (this.graph2Amount / this.totalAmount) * 100;
    },
    tooltipLinePositionPercent() {
      return (this.lineAmount / this.totalAmount) * 100;
    },
    graphLegendStep() {
      let result = this.totalAmount / this.graphLegendItemCount;
      return (
        Math.round(result / this.graphLegendScaleResolution) *
        this.graphLegendScaleResolution
      );
    },
    // graphLegendItemCount() {
    // let steps = this.totalAmount / this.graphLegendStep;
    // return Math.ceil(steps);
    // },
    graphLegendSteps() {
      let result = [];
      let number = 0;
      for (let i = 0; i <= this.graphLegendItemCount; i++) {
        result.push({
          percentSize: +this.graphLegendItemWidth + 0.7, // arbitrary to account for text size
          number: number,
        });

        number = +number + this.graphLegendStep;
      }
      // the last step overflows and should not be displayed (chancing step count will mess up the calculations, so dont)
      result.pop();
      return result;
    },
    graphLegendItemWidth() {
      return 100 / this.graphLegendItemCount;
    },
    lineBorderCssProperty() {
      return `1px dashed var(--c-${this.lineColor})`;
    },
    lineArrowBorderColorProperty() {
      return ` var(--c-${this.tooltipBackgroundColor}) transparent transparent transparent`;
    },
    graphLegendLinesCssColor() {
      return `var(--c-${this.lineColor})`
    }
  },
  methods: {},
};
</script>

<style scoped lang="scss">
.graphComparison1 {
  .title-block {
    margin-bottom: calc(var(--margin-max) * 0.9);

    @media (max-width: 1024px) {
      margin-bottom: calc(var(--margin-max) * 0.7);
    }

    .section-title {
      font-size: calc(var(--font-max) * 0.9);
      font-weight: 600;

      @media (max-width: 1024px) {
        margin-bottom: calc(var(--margin-max) * 0.7);
      }
    }
  }

  .main-graph-area {
    position: relative;
  }

  .tooltip-area {
    position: relative;
    height: 100px;
    width: 100%;
    z-index: 2;
  }

  .line-indicator {
    position: absolute;
    left: -1px;
    top: 0;
    z-index: 2;
    height: 1800px;
    width: 0;
    opacity: 0.9;
    border-inline-start: v-bind(lineBorderCssProperty);
  }

  .tooltip {
    position: absolute;
    transform: translateX(-50%);
    inset-inline-end: 0;
    top: 0;
    z-index: 2;

    @media (max-width: 767px) {
      max-width: 200px;
      transform: translateX(-10%);
    }

    @media (max-width: 767px) {
      max-width: 120px;
    }

    .tooltip__arrow {
      position: absolute;
      top: 100%;
      left: 50%;
      margin-inline-end: -4px;
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 10px 4px 0 4px;
      border-color: v-bind(lineArrowBorderColorProperty);

      @media (max-width: 767px) {
        left: 10%;
      }
    }


  }

  .graph-area {
    position: relative;
    border-right: 1px solid v-bind(graphLegendLinesCssColor);
    padding-bottom: var(--margin-xl);
    z-index: 1;
  }

  .bar {
    position: relative;
    min-height: 100px;
    width: 100%;
    border-radius: var(--border-radius-s) 0 0 var(--border-radius-s);
    z-index: 1;

    & + .bar {
      margin-top: var(--margin-xl);
    }

    @media (max-width: 1024px) {
      flex-wrap: wrap;
      justify-content: flex-start;
      padding: var(--margin-m);

      > * {
        flex: 100%;
      }
    }

    &__text-main {
      font-size: var(--font-xl);
      line-height: 1.1;

      @media (max-width: 767px) {
        font-size: var(--font-l);
        margin-bottom: var(--margin-s);
      }
    }

    &__text-secondary {
      font-size: var(--font-s);
      line-height: 1.1;

      @media (max-width: 1024px) {
        margin-bottom: var(--margin-m);
      }
    }

    &__number {
      font-size: var(--font-max);

      @media (max-width: 767px) {
        font-size: var(--font-xl);
      }
    }
  }

  .scales-area {
    position: relative;
    height: 40px;
    width: 100%;
    border-top: 1px solid v-bind(graphLegendLinesCssColor);
  }

  .scale-unit {
    position: relative;
    width: 2px;
    flex: 0 0 2px;
    height: var(--margin-s);

    @media (max-width: 767px) {
      &:nth-child(2n + 1) {
        .scale-unit__text {
          opacity: 0;
        }
      }
    }

    &:first-of-type {
      inset-inline-start: 1px;
    }

    &__marker {
      position: absolute;
      top: 0;
      width: 1px;
      flex: 0 0 1px;
      height: var(--margin-m);
      background: v-bind(graphLegendLinesCssColor);
      transform: translateX(50%);
    }

    &__text {
      position: absolute;
      top: calc(100% + var(--margin-m));
      transform: translateX(50%);
      font-size: var(--font-m);

      @media (max-width: 767px) {
        font-size: var(--font-s);
      }
    }
  }
}
</style>
